import React, { useEffect, useMemo, useRef, useCallback } from 'react'
import { makeStyles } from '@material-ui/styles'
import MiniTimeline from './MiniTimeline/MiniTimeline'
import useTimelimeItems from './useTimelineItems'
import { useDispatch, useSelector } from 'react-redux'
import Actions from 'actions'
import { AsyncStatus } from 'consts'
import ScanFeedSkeleton from './Skeletons/ScanFeedSkeleton'
import useTimelineItemSelection from './useTimelineItemSelection'
import useLoadPatientTimelineItems from './useLoadPatientTimelineItems'
import ScanFeed from './ScanFeed/ScanFeed'
import { scrollBar } from 'styles/theme'
import TimelineEmptyState from './TimelineEmptyState'
import PatientBanner from './PatientBanner'
import MainFeed from './MainFeed/MainFeed'
import MiniTimelineSkeleton from './Skeletons/MiniTimelineSkeleton'
import useMiniTimelineThemes from './MiniTimeline/Themes/useMiniTimelineThemes'
import TimelineViewToggle from './TimelineViewToggle'
import TimeBasedMiniTimeline from './MiniTimeline/TimeBased/TimeBasedMiniTimeline'
import useTimeBasedMiniTimelineItems from './MiniTimeline/TimeBased/useTimeBasedMiniTimelineItems'
import { MiniTimelineThemes } from 'consts/timelineConsts'

const useStyles = makeStyles(theme => ({
  timelineBody: {
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    height: 'calc(100% - var(--patient-banner-height))'
  },
  miniTimelineContainer: {
    height: ({ withThemeSelector }) => `calc(100% - 88px${withThemeSelector ? ' - 45px' : ''})` // 88px = back button (38px) + scroll to top (25px) + scroll to bottom (25px)
  },
  feedContainer: {
    flex: 1,
    padding: '0 16px 16px 4px',
    height: '100%',
    overflowY: 'scroll',
    ...scrollBar,
    '&::-webkit-scrollbar': {
      background: 'var(--bg-color-timelineV2)',
      width: 5
    }
  }
}))

const Timeline = ({ patientSwitchableViewRef }) => {
  const dispatch = useDispatch()
  const timelineBodyRef = useRef()

  const { isLoadingPatient } = useSelector(state => state.patientsReducer)
  const { loadStatus: timelineItemsLoadStatus, data: patientTimelineItems } = useSelector(
    state => state.timelineReducer.mainFeed.timelineItems
  )

  useLoadPatientTimelineItems()
  const { selectedTimelineItemId, setSelectedTimelineItem, clearSelectedTimelineItem } = useTimelineItemSelection()
  const { miniTimelineItems, mainFeedItems, hasAnyScans, totalScans } = useTimelimeItems({
    patientTimelineItems: patientTimelineItems
  })
  const { selectedTheme, selectTheme, themeOptions } = useMiniTimelineThemes()
  // The time-based axis is one of the views in the theme selector, shown to practices on the flag.
  // The selected theme survives a patient switch while the available options are re-resolved in an
  // effect, so both are checked - otherwise moving to a practice without the flag renders this view,
  // and fetches its cycles, for a frame.
  const isTimeBasedSelected = useMemo(
    () =>
      selectedTheme === MiniTimelineThemes.TimeBased &&
      themeOptions.some(option => option.type === MiniTimelineThemes.TimeBased),
    [selectedTheme, themeOptions]
  )
  const { timeBasedItems, missedScansUnavailable } = useTimeBasedMiniTimelineItems({
    miniTimelineItems,
    isEnabled: isTimeBasedSelected
  })

  const isLoadingTimelineItems = useMemo(
    () => timelineItemsLoadStatus === AsyncStatus.Loading,
    [timelineItemsLoadStatus]
  )

  const analyticsPayload = useMemo(
    () => ({
      totalDisplayedScans: totalScans
    }),
    [totalScans]
  )

  const handleShowScanFeed = useCallback(() => {
    const lastScan = miniTimelineItems.filter(item => item.type === 'grinScan').slice(-1)[0]
    if (lastScan) {
      setSelectedTimelineItem({ timelineItemId: lastScan.id })
    }
  }, [miniTimelineItems, setSelectedTimelineItem])

  useEffect(() => {
    if (timelineItemsLoadStatus === AsyncStatus.Completed) {
      dispatch(
        Actions.selectTimelineItem({
          timelineItemId: selectedTimelineItemId
        })
      )
    }
  }, [selectedTimelineItemId, timelineItemsLoadStatus, dispatch])

  const classes = useStyles({
    withThemeSelector: themeOptions.length > 1
  })

  return (
    <>
      <PatientBanner isLoading={isLoadingPatient} />
      <div className={classes.timelineBody} ref={timelineBodyRef}>
        {isLoadingTimelineItems ? (
          <div className={classes.miniTimelineContainer}>
            <MiniTimelineSkeleton />
          </div>
        ) : hasAnyScans ? (
          <div className={classes.miniTimelineContainer}>
            <TimelineViewToggle
              isScanFeed={!!selectedTimelineItemId}
              onShowMainFeed={clearSelectedTimelineItem}
              onShowScanFeed={handleShowScanFeed}
            />
            {isTimeBasedSelected ? (
              <TimeBasedMiniTimeline
                timelineItems={timeBasedItems}
                missedScansUnavailable={missedScansUnavailable}
                selectedTimelineItemId={selectedTimelineItemId}
                setSelectedTimelineItem={setSelectedTimelineItem}
                selectedTheme={selectedTheme}
                onSelectTheme={selectTheme}
                themeOptions={themeOptions}
                analyticsPayload={analyticsPayload}
              />
            ) : (
              <MiniTimeline
                timelineItems={miniTimelineItems}
                selectedTimelineItemId={selectedTimelineItemId}
                setSelectedTimelineItem={setSelectedTimelineItem}
                selectedTheme={selectedTheme}
                onSelectTheme={selectTheme}
                themeOptions={themeOptions}
                analyticsPayload={analyticsPayload}
              />
            )}
          </div>
        ) : (
          <></>
        )}
        <div className={classes.feedContainer}>
          {isLoadingTimelineItems ? (
            <ScanFeedSkeleton />
          ) : hasAnyScans ? (
            selectedTimelineItemId ? (
              <ScanFeed patientSwitchableViewRef={patientSwitchableViewRef} />
            ) : (
              <MainFeed
                timelineItems={mainFeedItems}
                setSelectedTimelineItem={setSelectedTimelineItem}
                analyticsPayload={analyticsPayload}
              />
            )
          ) : (
            <TimelineEmptyState />
          )}
        </div>
      </div>
    </>
  )
}

export default Timeline
